import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantUi } from 'corso-types';
import api from '~/api';
import { useShippingClaim } from '~/features/shippingClaimReview/ShippingClaimReviewProvider';
import { useStoreId } from '~/providers/StoreUserProvider';

/**
 * A prefix for all tag-based queries for the purposes of easy invalidation.
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/query-invalidation
 */
const queryKeyPrefix = 'shippingClaimTags';

// * store shipping claim tags

export function useStoreShippingClaimTagsCreate() {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) =>
      api.store(storeId).storeShippingClaimTags.create({ name }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }),
  });
}

/** Available shipping claim tags across the store. */
export function useStoreShippingClaimTags<
  Data = CrewMerchantUi.ShippingClaimTag[],
>(select?: (tags: { id: number; name: string }[]) => Data) {
  const storeId = useStoreId();
  return useQuery({
    queryKey: [queryKeyPrefix, storeId],
    queryFn: () => api.store(storeId).storeShippingClaimTags.list(),
    select,
  });
}

/** Delete a shipping claim tag from the store. */
export function useStoreShippingClaimTagDelete() {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: number) =>
      api.store(storeId).storeShippingClaimTags.delete(`${tagId}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }),
  });
}

// * shipping claim tags // * only available within the context of a shipping claim

/** Query key for the tags associated with a specific shipping claim. */
function useShippingClaimTagsQueryKey() {
  const storeId = useStoreId();
  const { id } = useShippingClaim();

  return [queryKeyPrefix, storeId, `${id}`] as const;
}

/** Current tags for the shipping claim. */
export function useShippingClaimTags() {
  const queryKey = useShippingClaimTagsQueryKey();
  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [, storeId, shippingClaimId] }) =>
      api.store(storeId).shippingProtection.claim(shippingClaimId).tags.list(),
  });
}

/** Upsert tags for a specific shipping claim. */
export function useShippingClaimTagsUpsert() {
  const queryClient = useQueryClient();
  const [, storeId, shippingClaimId] = useShippingClaimTagsQueryKey();

  return useMutation({
    mutationFn: (tags: string[]) =>
      api
        .store(storeId)
        .shippingProtection.claim(shippingClaimId)
        .tags.upsert(tags.map((name) => ({ name }))),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }), // invalidate any queries with the query key prefix for the store
  });
}
